import Big from 'big.js'
import { fromUnit } from 'metronome-utils'
import useTranslation from 'next-translate/useTranslation'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

import { usePrevious } from '../hooks/usePrevious'

import CryptoNumber from './CryptoNumber'
import FiatNumber from './FiatNumber'
import IconContainer from './icons/IconContainer'
import { MetronomeContext } from './MetronomeContext'
import OperationUnitInput from './OperationUnitInput'
import TokenSelector from './TokenSelector'
import WalletContext from './WalletContext'

const OperationInputContext = createContext()

export default function OperationInput({
  autoFocus,
  availableTokens,
  balancePrefix,
  balanceSuffix,
  children,
  chooseLabel,
  disableMax,
  disableInput,
  disableSelector,
  hasSelectedToken,
  initialValue,
  inputClasses,
  inputProps,
  loading,
  onChange,
  onFocus,
  onTokenChange,
  selectedToken,
  userAssetBalance,
  disableWithoutBalance = true
}) {
  const { inputValue, setInputValue, synthBalancesLoaded } =
    useContext(MetronomeContext)
  const { wallet: connectedWallet } = useContext(WalletContext)
  const disabled =
    loading ||
    (disableWithoutBalance && userAssetBalance && !Big(userAssetBalance).gt(0))
  const disableInputs =
    disableInput ||
    disabled ||
    !connectedWallet ||
    !synthBalancesLoaded ||
    !selectedToken

  return (
    <OperationInputContext.Provider
      value={{
        autoFocus,
        availableTokens,
        balancePrefix,
        balanceSuffix,
        chooseLabel,
        disabled,
        disableInputs,
        disableMax,
        disableSelector,
        hasSelectedToken,
        initialValue,
        inputClasses,
        inputProps,
        inputValue,
        loading,
        onChange,
        onFocus,
        onTokenChange,
        selectedToken,
        setInputValue,
        userAssetBalance
      }}
    >
      {children}
    </OperationInputContext.Provider>
  )
}

OperationInput.TokenSelector = function OperationInputTokenSelector({
  className,
  disableWithoutBalance = true,
  displayTokenBalances,
  itemsListClassName
}) {
  const {
    availableTokens,
    chooseLabel,
    disableSelector,
    hasSelectedToken,
    loading,
    onTokenChange,
    selectedToken
  } = useContext(OperationInputContext)
  const [token, setToken] = useState()
  const previousToken = usePrevious(token)

  const handleTokenChange = useCallback(
    function (val) {
      onTokenChange?.(val)
    },
    [onTokenChange]
  )

  useEffect(
    function () {
      if (selectedToken) {
        setToken(selectedToken)
      } else {
        setToken(undefined)
      }
    },
    [selectedToken]
  )

  useEffect(
    function () {
      if (token?.address !== previousToken?.address && !hasSelectedToken) {
        handleTokenChange?.(token)
      }
    },
    [handleTokenChange, hasSelectedToken, previousToken?.address, token]
  )

  if (selectedToken && hasSelectedToken) {
    return (
      <div className="bg-blue-tertiary flex cursor-pointer items-center rounded-lg p-2 text-sm leading-[24px] text-white">
        <IconContainer
          className="mr-2 h-6 w-6"
          name={selectedToken.symbol.toLowerCase()}
        />
        {selectedToken.symbol}
      </div>
    )
  }
  const _availableTokens = displayTokenBalances
    ? availableTokens.sort(function (a, b) {
        const aBalance = fromUnit(a.balance || 0, a.decimals)
        const bBalance = fromUnit(b.balance || 0, b.decimals)
        return Big(aBalance).lt(bBalance)
          ? 1
          : Big(bBalance).lt(aBalance)
          ? -1
          : 0
      })
    : availableTokens

  return (
    <TokenSelector
      availableTokens={_availableTokens}
      chooseLabel={chooseLabel}
      className={`${
        selectedToken ? '' : '-mb-3.5 mt-3.5'
      } transition-all duration-200 ${className ? className : ''}`}
      disableWithoutBalance={disableWithoutBalance}
      disabled={disableSelector || loading}
      displayBalances={displayTokenBalances}
      itemsListClassName={itemsListClassName}
      setToken={setToken}
      token={token}
    />
  )
}

OperationInput.Input = function OperationInputInput() {
  const {
    autoFocus,
    balancePrefix,
    disableInputs,
    initialValue,
    inputClasses,
    inputValue,
    inputProps,
    onChange,
    onFocus,
    selectedToken,
    setInputValue
  } = useContext(OperationInputContext)

  return (
    <OperationUnitInput
      autoFocus={autoFocus}
      balancePrefix={balancePrefix}
      className={inputClasses}
      disableInputs={disableInputs}
      initialValue={initialValue}
      inputProps={inputProps}
      onChange={onChange}
      onFocus={onFocus}
      selectedToken={selectedToken}
      setValue={setInputValue}
      value={inputValue}
    />
  )
}

OperationInput.Balance = function OperationInputBalance({ isInvalid }) {
  const { balancePrefix, balanceSuffix, selectedToken, userAssetBalance } =
    useContext(OperationInputContext)
  const { synthBalancesLoaded } = useContext(MetronomeContext)
  const { wallet: connectedWallet } = useContext(WalletContext)

  if (!selectedToken || !connectedWallet) {
    return null
  }

  return (
    <div
      className={`flex min-w-[7rem] items-center justify-end ${
        isInvalid ? 'text-red-600' : ''
      }`}
    >
      <CryptoNumber
        loading={!synthBalancesLoaded}
        prefix={`${balancePrefix}: `}
        suffix={balanceSuffix ? balanceSuffix : ''}
        value={userAssetBalance || 0}
      />
    </div>
  )
}

OperationInput.ValueInUsd = function OperationInputValueInUsd({ usdRate }) {
  const { inputValue } = useContext(OperationInputContext)
  const { selectedTokenUsdRate } = useContext(MetronomeContext)
  const rate = usdRate || selectedTokenUsdRate
  const valueInUsd = rate ? Big(inputValue).mul(rate) : 0

  return <FiatNumber prefix="≈$" value={valueInUsd} />
}

OperationInput.MaxSelector = function OperationInputMaxSelector({
  disabled,
  onClick
}) {
  const { t } = useTranslation()
  const { selectedToken, userAssetBalance, setInputValue } = useContext(
    OperationInputContext
  )
  const { wallet: connectedWallet } = useContext(WalletContext)

  if (!selectedToken || !connectedWallet || disabled) {
    return null
  }

  const handleClick = function () {
    if (onClick) {
      onClick()
    }
    setInputValue(Big(userAssetBalance))
  }

  return (
    <span
      className={`text-teal-primary text-xs font-semibold uppercase ${
        !disabled ? 'cursor-pointer' : 'cursor-not-allowed'
      }`}
      data-sentry="max-selector"
      onClick={!disabled ? handleClick : undefined}
    >
      {t('max')}
    </span>
  )
}
